import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { list, remove, getUrl } from "aws-amplify/storage";
import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import type { Schema } from "schema/data/resource";
import outputs from "amplify_outputs.json";
import { CRICOSStatusLogItem } from "../../components/organisms/CRICOSStatusPanel/CRICOSStatusPanel";

Amplify.configure(outputs);

const client = generateClient<Schema>();

type FileMetadata = {
  path: string;
  lastModified?: Date;
  size?: number;
  eTag?: string;
};

type CricosActionType = 
  | "PROCESS_EXCEL"
  | "PROCESS_ZIP"
  | "INGEST_COURSES" 
  | "DELETE_COURSES" 
  | "INGEST_INSTITUTIONS" 
  | "DELETE_INSTITUTIONS"
  | "GET_FAVICONS" 
  | null 
  | undefined;

type IngestedData = {
  fileName: string;
  status: string;
};

interface CourseIngestionContextType {
  files: FileMetadata[];
  processedFiles: FileMetadata[];
  isLoading: boolean;
  isProcessedLoading: boolean;
  isDeleting: boolean;
  deleteError: string;
  fileStatusMap: { [key: string]: boolean };
  logs: CRICOSStatusLogItem[];
  logsLoading: boolean;
  ingestedCourses: IngestedData;
  ingestedInstitutions: IngestedData;
  ingestedFavicons: IngestedData;
  ingestedDataLoading: boolean;
  deleteFile: (filePath: string) => Promise<void>;
  processFile: (action: CricosActionType, filePath: string) => Promise<void>;
  downloadFile: (filePath: string) => Promise<void>;
  refreshFiles: () => Promise<void>;
}

const CourseIngestionContext = createContext<CourseIngestionContextType | undefined>(undefined);

export const useCourseIngestion = () => {
  const context = useContext(CourseIngestionContext);
  if (!context) {
    throw new Error("useCourseIngestion must be used within a CourseIngestionProvider");
  }
  return context;
};

interface CourseIngestionProviderProps {
  children: ReactNode;
}

export const CourseIngestionProvider: React.FC<CourseIngestionProviderProps> = ({ children }) => {
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [processedFiles, setProcessedFiles] = useState<FileMetadata[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isProcessedLoading, setIsProcessedLoading] = useState<boolean>(true);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string>("");
  const [tokenid, setTokenid] = useState<string | undefined>(undefined);
  const [fileStatusMap, setFileStatusMap] = useState<{ [key: string]: boolean }>({});
  const [logs, setLogs] = useState<CRICOSStatusLogItem[]>([]);
  const [logsLoading, setLogsLoading] = useState<boolean>(true);
  const [ingestedCourses, setIngestedCourses] = useState<IngestedData>({ fileName: '', status: '' });
  const [ingestedInstitutions, setIngestedInstitutions] = useState<IngestedData>({ fileName: '', status: '' });
  const [ingestedFavicons, setIngestedFavicons] = useState<IngestedData>({ fileName: '', status: '' });
  const [ingestedDataLoading, setIngestedDataLoading] = useState<boolean>(false);

  const subscriptionRef = useRef<any>(null);

  const checkFileStatus = useCallback(async (fileName: string): Promise<boolean> => {
    try {
      const response = await client.models.CRICOSStatusLog.byFileName(
        { fileName },
        { 
          limit: 1,
          sortDirection: "DESC",
          authMode: "userPool",
          authToken: tokenid
        }
      );
      
      if (response?.data && response.data.length > 0) {
        const latestStatus = response.data[0].status || '';
        return !latestStatus.includes('COMPLETED');
      }
      return false;
    } catch (error) {
      console.error(`Error checking status for file ${fileName}:`, error);
      return false;
    }
  }, [tokenid]);

  const updateFileStatuses = useCallback(async (fileItems: FileMetadata[]) => {
    const newStatusMap: { [key: string]: boolean } = {};
    
    for (const file of fileItems) {
      const fileName = file.path.split('/').at(-1) || '';
      try {
        const isProcessing = await checkFileStatus(fileName);
        newStatusMap[fileName] = isProcessing;
      } catch (error) {
        console.error(`Error checking status for ${fileName}:`, error);
        newStatusMap[fileName] = false;
      }
    }
    
    setFileStatusMap(newStatusMap);
  }, [checkFileStatus]);

  const listFilesInSpecificPath = useCallback(async (pathToList: string, isProcessedPath: boolean = false) => {
    if (isProcessedPath) {
      setIsProcessedLoading(true);
    } else {
      setIsLoading(true);
    }
    try {
      const result = await list({
        path: pathToList,
        options: {
          bucket: "EdubayBucket",
          listAll: true,
        },
      });

      if (isProcessedPath) {
        setProcessedFiles(result.items);
      } else {
        setFiles(result.items);
        
        if (result.items.length > 0) {
          updateFileStatuses(result.items);
        }
      }

    } catch (error) {
      console.error('Error listing files:', error);
    } finally {
      if (isProcessedPath) {
        setIsProcessedLoading(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [updateFileStatuses]);

  const deleteFile = useCallback(async (filePath: string) => {
    const fileName = filePath.split('/').at(-1) || '';
    setFileStatusMap(prev => ({
      ...prev,
      [fileName]: true
    }));
    
    setIsDeleting(true);
    setDeleteError("");
    try {
      await remove({
        path: filePath,
        options: {
          bucket: "EdubayBucket"
        }
      });
      setIsDeleting(false);
      
      listFilesInSpecificPath('private/CRICOS/Origin/');
      
      setFileStatusMap(prev => {
        const updated = { ...prev };
        delete updated[fileName];
        return updated;
      });
      
    } catch (error) {
      console.error('Error deleting file:', error);
      setDeleteError("Failed to delete file. Please try again.");
      setFileStatusMap(prev => ({
        ...prev,
        [fileName]: false
      }));
    }
  }, [listFilesInSpecificPath]);

  const processFile = useCallback(async (action: CricosActionType, filePath: string) => {
    const fileName = filePath.split('/').at(-1) || '';
    setFileStatusMap(prev => ({
      ...prev,
      [fileName]: true
    }));
    
    try {
      await client.mutations.processCricos(
        {
          action,
          filePath
        },
        { 
          authMode: "userPool",
          authToken: tokenid
        }
      );
      
      if (action === 'INGEST_COURSES' || action === 'DELETE_COURSES') {
        setIngestedCourses({
          fileName: fileName,
          status: "PROCESSING"
        });
      }

      if (action === 'INGEST_INSTITUTIONS' || action === 'DELETE_INSTITUTIONS') {
        setIngestedInstitutions({
          fileName: fileName,
          status: "PROCESSING"
        });
      }

      if (action === 'GET_FAVICONS') {
        setIngestedFavicons({
          fileName: fileName,
          status: "PROCESSING"
        });
      }
      
    } catch (error) {
      console.error('Error processing file:', error);
      setDeleteError("Failed to process file. Please try again.");
      setFileStatusMap(prev => ({
        ...prev,
        [fileName]: false
      }));
    }
  }, [tokenid]);

  const downloadFile = useCallback(async (filePath: string) => {
    try {
      const result = await getUrl({
        path: filePath,
        options: {
          bucket: "EdubayBucket"
        }
      });
      
      const link = document.createElement('a');
      link.href = result.url.toString();
      link.download = filePath.split('/').at(-1) || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  }, []);

  const fetchIngestedData = useCallback(async () => {
    setIngestedDataLoading(true);
    try {
      const coursesResponse: any = await client.models.CRICOSIngested.get(
        { id: "COURSES" },
        { authMode: "userPool", authToken: tokenid }
      );
      
      const institutionsResponse: any = await client.models.CRICOSIngested.get(
        { id: "INSTITUTIONS" },
        { authMode: "userPool", authToken: tokenid }
      );

      const faviconsResponse: any = await client.models.CRICOSIngested.get(
        { id: "FAVICONS" },
        { authMode: "userPool", authToken: tokenid }
      );
      
      setIngestedCourses({
        fileName: coursesResponse.data?.fileName || '',
        status: coursesResponse.data?.status || ''
      });
      
      setIngestedInstitutions({
        fileName: institutionsResponse.data?.fileName || '',
        status: institutionsResponse.data?.status || ''
      });

      setIngestedFavicons({
        fileName: faviconsResponse.data?.fileName || '',
        status: faviconsResponse.data?.status || ''
      });
    } catch (error) {
      console.error('Error fetching ingested data:', error);
    } finally {
      setIngestedDataLoading(false);
    }
  }, [tokenid]);

  const fetchLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      
      const response = await client.models.CRICOSStatusLog.byGlobalPK(
        {
          globalPK: 'GLOBAL_PK',
        },
        { 
          limit: 50,
          sortDirection: "DESC", 
          authMode: "userPool",
          authToken: tokenid
        }
      );
      
      if (response?.data) {
        setLogs(response.data.map(log => ({
          id: log.id,
          fileName: log.fileName,
          status: log.status || 'UNKNOWN',
          message: log.message,
          createdAt: new Date(log.createdAt || '').toLocaleString()
        })));
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, [tokenid]);

  const refreshFiles = useCallback(async () => {
    await listFilesInSpecificPath('private/CRICOS/Origin/');
    await listFilesInSpecificPath('private/CRICOS/Processed/', true);
  }, [listFilesInSpecificPath]);

  const handleNewLog = useCallback((data: any) => {
    const newLog = data;
    
    setLogs(prevLogs => {
      const existingLogIndex = prevLogs.findIndex(log => log.id === newLog.id);
      
      const formattedLog = {
        id: existingLogIndex >= 0 ? `${newLog.id}-${Date.now()}` : newLog.id,
        fileName: newLog.fileName,
        status: newLog.status || 'UNKNOWN',
        message: newLog.message,
        createdAt: new Date(newLog.createdAt || '').toLocaleString()
      };
      
      let updatedLogs;
      if (existingLogIndex >= 0) {
        updatedLogs = [...prevLogs];
        updatedLogs[existingLogIndex] = formattedLog;
      } else {
        updatedLogs = [formattedLog, ...prevLogs];
      }
      
      return updatedLogs.slice(0, 50);
    });
    
    if (newLog.fileName) {
      const status = newLog.status || '';
      const isProcessing = !status.includes('COMPLETED');
      
      setFileStatusMap(prev => ({
        ...prev,
        [newLog.fileName]: isProcessing
      }));
      
      if (status.includes('COMPLETED') || status.includes('ERROR')) {
        refreshFiles();
        
        if (status.includes('COURSES') || status.includes('INSTITUTIONS')|| status.includes('FAVICONS')) {
          fetchIngestedData();
        }
      }
    }
  }, [refreshFiles, fetchIngestedData]);

  useEffect(() => {
    async function fetchUserIdAndInitialize() {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        
        setTokenid(token);
        
        if (token) {
          await listFilesInSpecificPath('private/CRICOS/Origin/');
          await listFilesInSpecificPath('private/CRICOS/Processed/', true);
          await fetchLogs();
          await fetchIngestedData();
        }
      } catch (error) {
        console.error("Error getting TokenId:", error);
      }
    }
    
    fetchUserIdAndInitialize();
  }, [listFilesInSpecificPath, fetchLogs, fetchIngestedData]);

  useEffect(() => {
    if (!tokenid) return;
    
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }
    
    try {
      const subscription = client.models.CRICOSStatusLog.onCreate({
        authMode: "userPool",
        authToken: tokenid
      });
      
      subscriptionRef.current = subscription.subscribe({
        next: (data: any) => {
          handleNewLog(data);
        },
        error: (error) => {
          console.error('Subscription error:', error);
          
          if (error?.errors?.[0]?.message === 'Unauthorized') {
            setTimeout(() => {
              fetchAuthSession().then(session => {
                const token = session.tokens?.idToken?.toString();
                if (token) {
                  setTokenid(token);
                }
              }).catch(err => {
                console.error('Failed to refresh auth session:', err);
              });
            }, 5000);
          }
        }
      });
      
    } catch (error) {
      console.error('Error setting up subscription:', error);
    }
    
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [tokenid, handleNewLog]);

  const value: CourseIngestionContextType = {
    files,
    processedFiles,
    isLoading,
    isProcessedLoading,
    isDeleting,
    deleteError,
    fileStatusMap,
    logs,
    logsLoading,
    ingestedCourses,
    ingestedInstitutions,
    ingestedFavicons,
    ingestedDataLoading,
    deleteFile,
    processFile,
    downloadFile,
    refreshFiles,
  };

  return (
    <CourseIngestionContext.Provider value={value}>
      {children}
    </CourseIngestionContext.Provider>
  );
};

export default CourseIngestionProvider;